import { ColumnDef } from "@tanstack/react-table";
import { PurchaseReportData } from "@snackpass/snackpass-types";
import { useSelector } from "react-redux";
import { useMemo } from "react";

import { DataTableColumnHeader } from "src/@/components/ui/data-table/table-column-header";
import { getActiveStore } from "src/redux/selectors";
import { getStoreTimezone } from "#utils/helpers";

import {
    getOrderColumns,
    OrderField,
    getBridgeKitchenStatus,
    getBridgePaymentStatus,
} from "../../lib";

import { OrderHistoryDetailsButton } from "./button";
import { Status } from "./order-status";

export type OrderColumnDef = ColumnDef<PurchaseReportData>;

export const useOrderColumns = (
    isSnackTeam: boolean,
    isBridgeDataUsed: boolean,
): OrderColumnDef[] => {
    const activeStore = useSelector(getActiveStore);
    const timezone = getStoreTimezone(activeStore);

    return useMemo(
        () =>
            Object.values(getOrderColumns())
                .filter((col) => {
                    if (col.snackTeamOnly && !isSnackTeam) return false;
                    if (
                        col.key === OrderField.KitchenStatus ||
                        col.key === OrderField.PaymentStatus
                    )
                        return isBridgeDataUsed;
                    if (col.key === OrderField.Status) return !isBridgeDataUsed;
                    return true;
                })
                .map((col) => ({
                    id: col.key,
                    header: ({ column }) => (
                        <DataTableColumnHeader
                            buttonClassName="bg-transparent"
                            hideIconUntilSorted
                            column={column}
                            title={col.label}
                        />
                    ),
                    accessorFn: (row) => col.accessor(row),
                    cell: ({ row }) => {
                        const data = row.original;

                        if (col.key === OrderField.Details) {
                            return (
                                <OrderHistoryDetailsButton
                                    text="View"
                                    value={data?.purchaseId ?? ""}
                                />
                            );
                        }

                        if (col.key === OrderField.Status) {
                            return <Status purchaseReportData={data} />;
                        }

                        if (col.key === OrderField.KitchenStatus) {
                            const enhancedData = data as any;
                            if (!enhancedData.bridge) {
                                return <span className="whitespace-nowrap">—</span>;
                            }
                            const status = getBridgeKitchenStatus(
                                enhancedData.bridge,
                            );
                            return <span className="whitespace-nowrap">{status}</span>;
                        }

                        if (col.key === OrderField.PaymentStatus) {
                            const enhancedData = data as any;
                            if (!enhancedData.bridge) {
                                return <span className="whitespace-nowrap">—</span>;
                            }
                            const status = getBridgePaymentStatus(
                                enhancedData.bridge,
                            );
                            return <span className="whitespace-nowrap">{status}</span>;
                        }

                        const formatted = col.formatter(
                            col.accessor(data),
                            data,
                            col.key === OrderField.PaidOutOn ? timezone : undefined,
                        );
                        return (
                            <span className="whitespace-nowrap">
                                {formatted.readable}
                            </span>
                        );
                    },
                })),
        [isBridgeDataUsed, isSnackTeam, timezone],
    );
};
